import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "drv-form-root" }
const _hoisted_2 = { class: "drv-text-body-md drv-text-weight-medium" }

import CheckConnection from "../CheckConnection.vue";
import HelpLink from "../HelpLink.vue";
import { computed, ref } from "vue";
import {
  ETelematicsMessagesKeys,
  IPlatform,
  IBaseToken,
  EAuthTypes,
  ECommonMessages,
  EPlatformName,
} from "../../../../types";
import { Input } from "draivn-ui-kit/components/input";
import {
  maxLengthValidator,
  minLengthValidator,
  requiredValidator,
} from "draivn-ui-domain";
import { HELP_URL_BY_PLATFORM } from "../../../../constants";


export default /*@__PURE__*/_defineComponent({
  __name: 'BaseToken',
  props: {
    modelValue: {},
    platform: {},
    disable: { type: Boolean }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const isDisabled = computed<boolean>(() => {
  return props.disable || isLoading.value;
});

const isLoading = ref<boolean>(false);

const isConnectionValid = ref<boolean | null>(
  props.modelValue && props.modelValue.type === EAuthTypes.TOKEN ? true : null
);

const emit = __emit;

const EMPTY_MODEL = { type: EAuthTypes.TOKEN, token: "" };

const model = computed<IBaseToken>({
  get() {
    return props.modelValue ?? EMPTY_MODEL;
  },
  set(value: IBaseToken) {
    emit("update:modelValue", value);
  },
});

const tokenModel = computed<string>({
  get() {
    return props.modelValue?.token ?? EMPTY_MODEL.token;
  },
  set(value: string) {
    model.value = { ...model.value, token: value };
  },
});

const currentMethod = computed(() => {
  return props.platform.methods.find(
    (m) => m.authorityType === EAuthTypes.TOKEN
  );
});
const url = computed<string | null>(() => {
  return currentMethod.value?.url ?? null;
});

const clearConnection = () => {
  tokenModel.value = "";
};

const tokenControl = ref<typeof Input | null>(null);

const hasFormError = computed<boolean>(() => {
  return !tokenModel.value || tokenControl.value?.hasError;
});

const hasError = computed<boolean>(() => {
  return hasFormError.value || !isConnectionValid.value;
});

const TRACKENSURE_TOKEN_TEXT = {
  title: ETelematicsMessagesKeys.TRACKENSURE_TOKEN_TITLE,
  inputTitle: ETelematicsMessagesKeys.TRACKENSURE_TOKEN_FIELD_TITLE,
};

const EZTOTRACK_TOKEN_TEXT = {
  title: ETelematicsMessagesKeys.EZTOTRACK_TOKEN_TITLE,
  inputTitle: ETelematicsMessagesKeys.EZTOTRACK_TOKEN_FIELD_TITLE,
};

const TT_ELD_TOKEN_TEXT = {
  title: ETelematicsMessagesKeys.TT_ELD_BASIC_TITLE,
  inputTitle: ETelematicsMessagesKeys.TT_ELD_BASIC_API_KEY_TITLE,
};

const PAYD_ELD_TOKEN_TEXT = {
  title: ETelematicsMessagesKeys.PAYD_ELD_TOKEN_TITLE,
  inputTitle: ETelematicsMessagesKeys.PAYD_ELD_TOKEN_FIELD_TITLE,
};

const HUTCH_SYSTEMS_TOKEN_TEXT = {
  title: ETelematicsMessagesKeys.HUTCH_SYSTEMS_TOKEN_TITLE,
  inputTitle: ETelematicsMessagesKeys.HUTCH_SYSTEMS_TOKEN_FIELD_TITLE,
};

const textByPlatformName: Partial<
  Record<
    EPlatformName,
    {
      title: string;
      inputTitle: string;
    }
  >
> = {
  [EPlatformName.EZ_FLEET]: {
    title: ETelematicsMessagesKeys.EZ_FLEET_TOKEN_TITLE,
    inputTitle: ETelematicsMessagesKeys.EZ_FLEET_TOKEN_FIELD_TITLE,
  },
  // Trackensure resellers - shared label
  [EPlatformName.TRACKENSURE]: TRACKENSURE_TOKEN_TEXT,
  [EPlatformName.SWIFT_ELD]: TRACKENSURE_TOKEN_TEXT,
  [EPlatformName.AMBER_ELD]: TRACKENSURE_TOKEN_TEXT,
  [EPlatformName.CLUB_ELD]: TRACKENSURE_TOKEN_TEXT,
  [EPlatformName.ELD_PRO_SOLUTION]: TRACKENSURE_TOKEN_TEXT,
  [EPlatformName.PROTRACKING_ELD]: TRACKENSURE_TOKEN_TEXT,
  [EPlatformName.SHARP_ELD]: TRACKENSURE_TOKEN_TEXT,
  [EPlatformName.VISTA_ELD]: TRACKENSURE_TOKEN_TEXT,
  [EPlatformName.ZENITH_ELD]: TRACKENSURE_TOKEN_TEXT,
  [EPlatformName.ADA_ELD]: TRACKENSURE_TOKEN_TEXT,
  [EPlatformName.ALFA_ELD]: TRACKENSURE_TOKEN_TEXT,
  [EPlatformName.EZ_TO_TRACK]: EZTOTRACK_TOKEN_TEXT,
  [EPlatformName.GOOD_DEAL_GPS]: EZTOTRACK_TOKEN_TEXT,
  [EPlatformName.HOS_ELD]: EZTOTRACK_TOKEN_TEXT,
  [EPlatformName.TRANSIT_TRACKING]: EZTOTRACK_TOKEN_TEXT,
  [EPlatformName.MT_ELD]: EZTOTRACK_TOKEN_TEXT,
  [EPlatformName.EAGLE_ELD]: EZTOTRACK_TOKEN_TEXT,
  [EPlatformName.SMART_WAY_ELD]: EZTOTRACK_TOKEN_TEXT,
  [EPlatformName.OXEMON]: EZTOTRACK_TOKEN_TEXT,
  [EPlatformName.ROUTELINK_IO]: EZTOTRACK_TOKEN_TEXT,
  [EPlatformName.ELD4_TRUCKS]: EZTOTRACK_TOKEN_TEXT,
  [EPlatformName.ATINA_ELD]: EZTOTRACK_TOKEN_TEXT,
  [EPlatformName.UNIFY_ELD]: EZTOTRACK_TOKEN_TEXT,
  [EPlatformName.ELD_RUN]: EZTOTRACK_TOKEN_TEXT,
  [EPlatformName.TERA_HUB]: EZTOTRACK_TOKEN_TEXT,
  [EPlatformName.HOBBS_ELD]: EZTOTRACK_TOKEN_TEXT,
  [EPlatformName.PINNACLE_ELD]: EZTOTRACK_TOKEN_TEXT,
  [EPlatformName.MEMENTO_ELD]: EZTOTRACK_TOKEN_TEXT,
  [EPlatformName.HORIZONT_PATH_ELD]: EZTOTRACK_TOKEN_TEXT,
  [EPlatformName.NOMAD_ELD]: EZTOTRACK_TOKEN_TEXT,
  [EPlatformName.TT_ELD]: TT_ELD_TOKEN_TEXT,
  [EPlatformName.EVO_ELD]: TT_ELD_TOKEN_TEXT,
  [EPlatformName.ONTIME_LOGS]: TT_ELD_TOKEN_TEXT,
  [EPlatformName.ZIPPY_ELD]: TT_ELD_TOKEN_TEXT,
  [EPlatformName.ONTIME_ELD]: TT_ELD_TOKEN_TEXT,
  [EPlatformName.FIVE_ELD]: TT_ELD_TOKEN_TEXT,
  [EPlatformName.LOGFLEET_ELD]: TT_ELD_TOKEN_TEXT,
  [EPlatformName.SUN_ELD]: TT_ELD_TOKEN_TEXT,
  [EPlatformName.PAYD_ELD]: PAYD_ELD_TOKEN_TEXT,
  [EPlatformName.HUTCH_SYSTEMS]: HUTCH_SYSTEMS_TOKEN_TEXT,
};

const currentText = computed<{
  title: string;
  inputTitle: string;
}>(() => {
  return {
    title: ETelematicsMessagesKeys.BASE_TOKEN_TITLE,
    inputTitle: ETelematicsMessagesKeys.BASE_TOKEN_INPUT_TITLE,
    ...textByPlatformName[props.platform.const],
  };
});

__expose({
  hasError,
});

return (_ctx: any,_cache: any) => {
  const _component_d_password_input = _resolveComponent("d-password-input")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", null, [
      _createElementVNode("div", _hoisted_2, _toDisplayString(_ctx.$t(currentText.value.title, {
            platform: _ctx.platform.label,
          })), 1),
      _createVNode(HelpLink, {
        link: _ctx.$t(_unref(HELP_URL_BY_PLATFORM)[_ctx.platform.const] || '')
      }, null, 8, ["link"])
    ]),
    _createVNode(_component_d_password_input, {
      class: "drv-form-control-adaptive drv-my-2",
      ref_key: "tokenControl",
      ref: tokenControl,
      modelValue: tokenModel.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((tokenModel).value = $event)),
      disable: isDisabled.value,
      label: _ctx.$t(currentText.value.inputTitle),
      "lazy-rules": "",
      rules: [
        _unref(requiredValidator)(_ctx.$t(_unref(ECommonMessages).REQUIRED_ERROR)),
        _unref(maxLengthValidator)(
          600,
          _ctx.$t(_unref(ECommonMessages).MAX_LENGTH_ERROR, { length: 600 })
        ),
        _unref(minLengthValidator)(
          2,
          _ctx.$t(_unref(ECommonMessages).MIN_LENGTH_ERROR, { length: 2 })
        ),
      ]
    }, null, 8, ["modelValue", "disable", "label", "rules"]),
    (url.value && currentMethod.value?.check_connection)
      ? (_openBlock(), _createBlock(CheckConnection, {
          key: 0,
          class: "drv-py-2",
          modelValue: isConnectionValid.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((isConnectionValid).value = $event)),
          loading: isLoading.value,
          "onUpdate:loading": _cache[2] || (_cache[2] = ($event: any) => ((isLoading).value = $event)),
          "is-model-fullfiled": !hasFormError.value,
          "platform-type": _ctx.platform.const,
          url: url.value,
          creds: model.value,
          onClear: clearConnection
        }, null, 8, ["modelValue", "loading", "is-model-fullfiled", "platform-type", "url", "creds"]))
      : _createCommentVNode("", true)
  ]))
}
}

})